import { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { checkIfIndividualPdd, markAsIndividualPdd, revertToStandardPdd } from '../../api/pddReportTypeApi';

/**
 * Custom hook for managing PDD report type functionality
 * @param {string} projectIdent - The project identifier
 * @param {boolean} isPddProject - Whether this is a PDD project
 * @returns {Object} - Hook return object with state and methods
 */
export const usePddReportType = (projectIdent, isPddProject = false) => {
  const [isIndividualPdd, setIsIndividualPdd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  // Fetch initial PDD report type status
  const fetchPddReportType = useCallback(async () => {
    if (!projectIdent || !isPddProject) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await checkIfIndividualPdd(projectIdent);
      setIsIndividualPdd(response.isIndividualPDD || false);
    } catch (err) {
      console.error('Failed to fetch PDD report type:', err);
      setError(err.message || 'Failed to fetch report type');
      // Don't show error message for initial load as it might be expected
    } finally {
      setLoading(false);
    }
  }, [projectIdent, isPddProject]);

  // Handle report type change from Standard to Individual
  const changeToIndividual = useCallback(async () => {
    if (!projectIdent || !isPddProject) {
      return false;
    }

    try {
      setLoading(true);
      setError(null);
      await markAsIndividualPdd(projectIdent);
      setIsIndividualPdd(true);
      return true;
    } catch (err) {
      console.error('Failed to mark as individual PDD:', err);
      const errorMessage = err.message || 'Failed to change report type to Individual';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 4000,
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [projectIdent, isPddProject, enqueueSnackbar]);

  // Handle report type change from Individual to Standard
  const changeToStandard = useCallback(async () => {
    if (!projectIdent || !isPddProject) {
      return false;
    }

    try {
      setLoading(true);
      setError(null);
      await revertToStandardPdd(projectIdent);
      setIsIndividualPdd(false);
      return true;
    } catch (err) {
      console.error('Failed to revert to standard PDD:', err);
      const errorMessage = err.message || 'Failed to change report type to Standard';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 4000,
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [projectIdent, isPddProject, enqueueSnackbar]);

  // Handle manual report type toggle
  const toggleReportType = useCallback(async (newType) => {
    if (newType === 'individual' || newType === true) {
      return await changeToIndividual();
    } else if (newType === 'standard' || newType === false) {
      return await changeToStandard();
    }
    return false;
  }, [changeToIndividual, changeToStandard]);

  // Auto-fetch on mount and when dependencies change
  useEffect(() => {
    fetchPddReportType();
  }, [fetchPddReportType]);

  return {
    // State
    isIndividualPdd,
    loading,
    error,
    
    // Methods
    fetchPddReportType,
    changeToIndividual,
    changeToStandard,
    toggleReportType,
    
    // Computed values
    reportTypeLabel: isIndividualPdd ? 'Individual' : 'Standard',
    reportTypeValue: isIndividualPdd,
  };
};